<script setup lang="ts">
import { useHead } from '@unhead/vue';
import Divider from '@/layout/Divider.vue';
import Layout from '@/layout/Layout.vue';
import locations from '@/scripts/locations';

useHead({
  title: "Albano's Villa | Our Story",
  meta: [
    {
      name: 'description',
      content:
        "How Jim and Russell Albano turned a Michigan City family kitchen into a LaPorte County Italian institution in 1957 — the story of Albano's Villa.",
    },
  ],
});

const [michigan, laporte, johnson] = locations;
</script>

<template>
  <Layout sidebar="images-our-story">
    <h1>A Family's Leap of Faith</h1>
    <p>
      <strong>How Albano's Villa became a LaPorte County institution</strong>
    </p>
    <p>
      Sometimes facts are far more interesting than fiction. And sometimes it takes an impulse to
      spur a long-brewing idea into action. Such is the case with the history of Albano's Villa and
      the way this LaPorte County favorite came to be. It is almost too fantastic to believe. Jim
      Albano, Sr. had dreamt of owning a restaurant for a long time. And, together with his brother
      Russell, they turned that dream into a reality in a single spontaneous day, converting a
      family kitchen into an Italian Restaurant that would endure for decades.
    </p>
    <p>
      Over the years, the enterprise truly became a family affair. Jim and Russell had help from
      many of their siblings, along with a host of other members of the Albano clan rotating in to
      assist. "It would be an interesting movie," Russell said with a laugh as he recalled how he
      and Jim began the business more than 50 years ago.
    </p>
    <p>
      It happened on a hot summer day in 1957. Their mother, Rosalie, left that morning to visit
      family in Chicago. Jim had discussed the possibility of opening a restaurant for months, but
      most people had passed it off as a fantasy. On this day, however, he and Russell backed up the
      talk.
    </p>
    <p>
      Their house at 1804 S. Franklin Street in Michigan City had been vacated by their mother for a
      week, and they decided there was no more time to lose. Jim convinced Russell to take his '54
      Buick to the bank and use it as collateral on a $500 loan. Then they bought an oven and some
      kitchen supplies with the money.
    </p>
    <p>
      With no warning to poor Rosalie, they tore down the walls separating the living room and
      dining room. They painted the remaining walls green and opened for business.
    </p>
    <p>
      By the time Rosalie got home that afternoon, the first floor of her home had been transformed
      into a fully functional restaurant. It is no surprise that, initially, she did not take to the
      change well. She walked into the renovated kitchen, turned around, and walked back out and sat
      on the steps and cried. Jim often said she cried for a week.
    </p>
    <p>
      "She about passed out," Russell said. "The carpets and everything were rolled up on the side.
      She thought we were just talking, a lot of hot air. If I could have, I would have put
      everything back the way it was."
    </p>
    <p>
      But it was too late for that. And so, on June 14, 1957, Albano's Villa officially opened …
      with Rosalie fully on board and doing much of the cooking. People in Michigan City were
      immediately enticed by the new restaurant, and Albano's was flooded with business immediately.
      Jim and Russell stayed open until 2 a.m. on that first night of business, and they kept those
      late hours from that night on.
    </p>
    <p>
      The success only inspired them to improve. It wasn't long before they were adding on to the
      restaurant. The Albano brothers built a barroom and expanded the dining area. Eventually they
      tore the old sections of the house down - remaining open the whole time - leaving just the
      restaurant. Albano's moved down the street in 1969 to its current location at
      <a :href="michigan.mapUrl" target="_blank" rel="noopener">{{ michigan.address }}</a
      >.
    </p>
    <p>
      "My dad never sat still," Jim Albano, Jr. said. "He always had some idea he wanted to try."
      Those ideas included delivering pizza before most restaurant owners ever considered it,
      selling frozen pizzas in the grocery store before it was commonplace, and using a custom-made
      conveyer belt oven that cooked 30 pizzas at a time.
    </p>
    <p>
      Jim Albano, Jr. first started helping out at the restaurant when he was nine years old,
      standing on milk crates and sticking labels on frozen pizzas that were sent to the local
      grocery stores. He now carries on the legacy of his father. He and his mother, Anita, opened a
      carryout restaurant in LaPorte in 1989, later moving it to its current location at
      <a :href="laporte.mapUrl" target="_blank" rel="noopener">{{ laporte.address }}</a> in 1994. In
      2009, a third location opened at
      <a :href="johnson.mapUrl" target="_blank" rel="noopener">{{ johnson.address }}</a
      >.
    </p>
    <p>
      Each of the three restaurants pays tribute to the rich history of Albano's, depicting Rosalie
      and her husband, Frank, standing in front of the grocery store that they owned for many years.
      "We continue using the same recipes handed down through the generations," Jim., Jr. said. "We
      still use the same hand-crafted ingredients with the same cooking method. I am proud that
      Albano's has become a local favorite and I know many people have fond memories of eating or
      working here. We fully intend to keep the same level of quality and tradition for another 50
      years!"
    </p>
    <Divider />
  </Layout>
</template>
